import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, IconButton, CircularProgress, Snackbar, Alert, Stack, Divider, Dialog } from "@mui/material";
import { AppsOutlined, Add, Close, Inventory2Outlined, CheckCircleOutline, LocalShippingOutlined } from "@mui/icons-material";
import {
  fetchMonthlyOrders,
  fetchOrdersByCountry,
  fetchOrderStatusBreakdown,
  fetchRevenueTrends,
  fetchUsersSummary,
  fetchTherapistsSummary,
  fetchRegistrationTrends,
  fetchFormSubmissions,
  fetchLabTurnaround,
  fetchRecentActivities,
} from "../../Services/apiService";
import socketService from "../../Services/socketService";
import StatusPill from "../../components/StatusPill";
import { useAuth } from "../../authContext/authContext";

// Design tokens — see README bento-grid spec.
const C = {
  bg: "#F1F1F1",
  surface: "#FFFFFF",
  ink: "#282828",
  primary: "#16498A",
  tint1: "#4B7FBE",
  tint2: "#8FB2DA",
  tintBg: "#EAF1FA",
  gray1: "#9AA0A6",
  gray2: "#B9BDC2",
  gray3: "#C7CACD",
  gray4: "#CDD0D3",
  gray5: "#DEE0E2",
  gray6: "#E4E5E7",
  gray7: "#EDEDED",
  textSecondary: "#8a8a8a",
  textBody: "#4a4a4a",
  textFaint: "#a4a4a4",
  border: "rgba(40,40,40,0.06)",
};

// Fixed rank order for ranked/magnitude series (countries, pipeline statuses) — never cycled.
const RANK_COLORS = [C.primary, C.tint1, C.tint2, C.ink, C.gray1, C.gray2, C.gray3, C.gray4];
const OTHERS_COLOR = C.gray6;

const cardSx = { borderRadius: "24px", background: C.surface, border: `1px solid ${C.border}` };

const FONT = "'Roboto Condensed', -apple-system, BlinkMacSystemFont, sans-serif";

const formatMonthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-US", { month: "short" });
};

const formatTimeAgo = (timestamp) => {
  const diffInMs = Date.now() - new Date(timestamp);
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
};

// Catmull-Rom → cubic Bezier so revenue/registration lines stay smooth (no sharp corners).
function smoothLinePath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M${points[0].x} ${points[0].y} L${points[1].x} ${points[1].y}`;
  let d = `M${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x} ${p2.y}`;
  }
  return d;
}

// Builds a smooth SVG line+area path (and gridlines) from [{ m, v }] over a fixed viewBox.
function buildLineChart(values, width, height, top, bottom) {
  if (!values.length) return { points: [], linePath: "", areaPath: "", gridlines: [] };
  const max = Math.max(...values.map((v) => v.v), 1) * 1.15;
  const n = values.length;
  const dx = n > 1 ? width / (n - 1) : 0;
  const points = values.map((v, i) => ({
    m: v.m,
    v: v.v,
    x: Math.round(i * dx),
    y: Math.round(top + (1 - v.v / max) * (height - top - bottom)),
  }));
  const line = smoothLinePath(points);
  const area = `${line} L${points[points.length - 1].x} ${height - bottom} L${points[0].x} ${height - bottom} Z`;
  const gridlines = [0, 0.33, 0.66, 1].map((f) => ({ y: Math.round(top + f * (height - top - bottom)) }));
  return { points, linePath: line, areaPath: area, gridlines };
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// Donut segment path for interactive country chart (same look as conic-gradient).
function donutSegmentPath(cx, cy, rOuter, rInner, startPct, endPct) {
  const start = (startPct / 100) * 360;
  const end = (endPct / 100) * 360;
  const large = end - start > 180 ? 1 : 0;
  const os = polarToCartesian(cx, cy, rOuter, end);
  const oe = polarToCartesian(cx, cy, rOuter, start);
  const is = polarToCartesian(cx, cy, rInner, start);
  const ie = polarToCartesian(cx, cy, rInner, end);
  return [
    `M ${os.x} ${os.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 0 ${oe.x} ${oe.y}`,
    `L ${is.x} ${is.y}`,
    `A ${rInner} ${rInner} 0 ${large} 1 ${ie.x} ${ie.y}`,
    "Z",
  ].join(" ");
}

const barLivingSx = {
  transition: "filter 0.2s ease, opacity 0.2s ease",
  cursor: "pointer",
  "&:hover": { filter: "brightness(1.18)", opacity: 0.92 },
};

const colBarLivingSx = {
  transition: "filter 0.2s ease, transform 0.2s ease",
  transformOrigin: "bottom center",
  cursor: "pointer",
  "&:hover": { filter: "brightness(1.12)", transform: "scaleY(1.08)" },
};

function FulfillmentRing({ pct }) {
  const size = 82;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const gap = 14;
  const p = Math.max(0, Math.min(100, pct)) / 100;
  const usable = Math.max(0, circ - gap * 2);
  const filled = usable * p;
  const rest = usable * (1 - p);
  const cx = size / 2;
  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <Box component="svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} sx={{ display: "block" }}>
        {rest > 0.5 && (
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={C.gray7}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${rest} ${circ}`}
            strokeDashoffset={-(filled + gap)}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        )}
        {filled > 0.5 && (
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={C.primary}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circ}`}
            strokeDashoffset={0}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        )}
      </Box>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{Math.round(pct)}%</Typography>
      </Box>
    </Box>
  );
}

function CardHeader({ title, subtitle, right }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3, gap: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Stack>
  );
}

function Pill({ children, sx }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: "12px",
        fontWeight: 600,
        color: C.primary,
        background: C.tintBg,
        px: "12px",
        py: "6px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}


function StatCardLight({ icon, value, label, gridColumn }) {
  return (
    <Box sx={{ ...cardSx, gridColumn, gridRow: "span 2", p: "22px 24px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: C.gray7, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>{label}</Typography>
      </Box>
    </Box>
  );
}

function EmptyNote({ children }) {
  return (
    <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", py: 3 }}>{children}</Typography>
  );
}

function ActivityRow({ activity, index, isNew }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        p: "14px",
        borderRadius: "14px",
        background: isNew ? "rgba(22,73,138,0.06)" : index % 2 === 0 ? "#FAFAFA" : "#FFFFFF",
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: isNew ? C.primary : C.gray2, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600, lineHeight: 1.4 }}>{activity.activity_description}</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: "5px" }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 12, color: C.textFaint }}>{formatTimeAgo(activity.created_at)}</Typography>
          {activity.activity_type === "order_updated" && activity.metadata?.progress_status && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: C.textBody, background: C.gray7, px: "9px", py: "3px", borderRadius: "999px" }}>
              {activity.metadata.progress_status}
            </Box>
          )}
          {isNew && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: C.primary, background: C.tintBg, px: "9px", py: "3px", borderRadius: "999px" }}>
              NEW
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

const ACTIVITY_PAGE_SIZE = 20;

// Full activity history, opened from "View all activity". Pages forward with real
// offset-based pagination (limit/offset + pagination.hasMore), appending each page.
function ActivityDialog({ open, onClose, initialActivities, newActivityIds }) {
  const [items, setItems] = React.useState(initialActivities);
  const [offset, setOffset] = React.useState(initialActivities.length);
  const [total, setTotal] = React.useState(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);

  React.useEffect(() => {
    if (!open) return;
    setItems(initialActivities);
    setOffset(initialActivities.length);
    setTotal(null);
    setHasMore(true);
  }, [open, initialActivities]);

  const loadMore = React.useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchRecentActivities(ACTIVITY_PAGE_SIZE, offset);
      const activities = res.data?.activities || [];
      const pagination = res.data?.pagination;
      setItems((prev) => [...prev, ...activities]);
      setOffset((prev) => prev + activities.length);
      setTotal(pagination?.total ?? null);
      setHasMore(pagination ? pagination.hasMore : activities.length === ACTIVITY_PAGE_SIZE);
    } catch (err) {
      console.error("❌ Failed to load more activities:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [offset, loadingMore, hasMore]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 96) loadMore();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: "24px", fontFamily: FONT } } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: C.ink }}>Recent Activity</Typography>
          <Typography sx={{ fontSize: "12px", color: C.textSecondary, mt: "2px" }}>
            {total != null ? `${items.length.toLocaleString()} of ${total.toLocaleString()} loaded` : "Scroll to load older updates"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: C.textSecondary }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: C.border }} />
      <Box onScroll={handleScroll} sx={{ maxHeight: "70vh", overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: "2px" }}>
        {items.length > 0 ? (
          items.map((activity, index) => (
            <ActivityRow key={activity.id} activity={activity} index={index} isNew={newActivityIds.has(activity.id)} />
          ))
        ) : (
          <EmptyNote>No recent activities</EmptyNote>
        )}
        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} sx={{ color: C.ink }} />
          </Box>
        )}
        {!hasMore && items.length > 0 && <EmptyNote>You&apos;ve reached the end</EmptyNote>}
      </Box>
    </Dialog>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [data, setData] = React.useState({
    monthlyOrders: null,
    countryData: null,
    statusBreakdown: null,
    revenueTrends: null,
    usersSummary: null,
    therapistsSummary: null,
    registrationTrends: null,
    formSubmissions: null,
    labTurnaround: null,
    recentActivities: [],
    loading: true,
    error: null,
  });

  const [socketConnected, setSocketConnected] = React.useState(false);
  const [newActivityIds, setNewActivityIds] = React.useState(new Set());
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "info" });
  const [activityDialogOpen, setActivityDialogOpen] = React.useState(false);
  const [hoverCountry, setHoverCountry] = React.useState(null);
  const [hoverRevenue, setHoverRevenue] = React.useState(null);
  const [hoverReg, setHoverReg] = React.useState(null);

  const handleNewActivity = React.useCallback((newActivity) => {
    setData((prev) => ({ ...prev, recentActivities: [newActivity, ...prev.recentActivities].slice(0, 20) }));
    setNewActivityIds((prev) => new Set([...prev, newActivity.id]));
    setSnackbar({ open: true, message: `New activity: ${newActivity.activity_description}`, severity: "info" });
    setTimeout(() => {
      setNewActivityIds((prev) => {
        const s = new Set(prev);
        s.delete(newActivity.id);
        return s;
      });
    }, 5000);
  }, []);

  React.useEffect(() => {
    const load = async () => {
      try {
        setData((prev) => ({ ...prev, loading: true, error: null }));
        const [monthlyOrdersRes, countryDataRes, statusBreakdownRes, revenueTrendsRes, usersSummaryRes, therapistsSummaryRes, registrationTrendsRes, formSubmissionsRes, labTurnaroundRes, activitiesRes] =
          await Promise.all([
            fetchMonthlyOrders(),
            fetchOrdersByCountry(),
            fetchOrderStatusBreakdown(),
            fetchRevenueTrends(),
            fetchUsersSummary(),
            fetchTherapistsSummary(),
            fetchRegistrationTrends(),
            fetchFormSubmissions(),
            fetchLabTurnaround(),
            fetchRecentActivities(20),
          ]);
        setData({
          monthlyOrders: monthlyOrdersRes.data,
          countryData: countryDataRes.data,
          statusBreakdown: statusBreakdownRes.data,
          revenueTrends: revenueTrendsRes.data,
          usersSummary: usersSummaryRes.data,
          therapistsSummary: therapistsSummaryRes.data,
          registrationTrends: registrationTrendsRes.data,
          formSubmissions: formSubmissionsRes.data,
          labTurnaround: labTurnaroundRes.data,
          recentActivities: activitiesRes.data?.activities || [],
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error("❌ Error fetching dashboard data:", error);
        setData((prev) => ({ ...prev, loading: false, error: error.message }));
      }
    };
    load();
  }, []);

  React.useEffect(() => {
    const socket = socketService.connect();
    if (socket) {
      socket.on("connect", () => setSocketConnected(true));
      socket.on("disconnect", () => setSocketConnected(false));
      socket.on("connect_error", () => setSocketConnected(false));
      socketService.onNewActivity(handleNewActivity);
    }
    return () => socketService.offNewActivity();
  }, [handleNewActivity]);

  const { monthlyOrders, countryData, statusBreakdown, revenueTrends, usersSummary, therapistsSummary, registrationTrends, formSubmissions, labTurnaround, recentActivities } = data;

  // Fulfillment rate = fulfilled orders / total orders (this month).
  const fulfillmentPct = monthlyOrders?.total_orders ? (monthlyOrders.fulfilled_orders / monthlyOrders.total_orders) * 100 : 0;

  const countryItems = React.useMemo(() => {
    const countries = countryData?.countries || [];
    if (!countries.length) return [];
    const top = countries.slice(0, 6).map((c, i) => ({
      key: c.country_code,
      name: c.country,
      pct: parseFloat(c.percentage),
      color: RANK_COLORS[i % RANK_COLORS.length],
    }));
    if (countries.length > 6) {
      const othersPct = countries.slice(6).reduce((s, c) => s + parseFloat(c.percentage), 0);
      top.push({ key: "OTHER", name: "Others", pct: othersPct, color: OTHERS_COLOR });
    }
    let acc = 0;
    return top.map((c) => {
      const start = acc;
      acc += c.pct;
      return { ...c, startPct: start, endPct: acc };
    });
  }, [countryData]);

  const pipelineItems = React.useMemo(() => {
    const statuses = statusBreakdown?.statuses || [];
    const maxPct = Math.max(...statuses.map((s) => parseFloat(s.percentage)), 1);
    return statuses.map((s, i) => ({
      key: s.status,
      name: s.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      count: s.count,
      pct: parseFloat(s.percentage),
      width: `${Math.max(3, (parseFloat(s.percentage) / maxPct) * 100)}%`,
      color: RANK_COLORS[i % RANK_COLORS.length],
    }));
  }, [statusBreakdown]);

  const usersRoleItems = React.useMemo(() => {
    const byRole = usersSummary?.by_role;
    if (!byRole) return [];
    const order = [
      { key: "admin", name: "Admin", color: C.primary },
      { key: "operator", name: "Operator", color: C.tint1 },
      { key: "therapist", name: "Therapist", color: C.ink },
      { key: "patient", name: "Patient", color: C.gray3 },
    ];
    const max = Math.max(...order.map((r) => byRole[r.key] || 0), 1);
    return order
      .filter((r) => byRole[r.key] !== undefined)
      .map((r) => ({ ...r, count: byRole[r.key], width: `${Math.max(2, (byRole[r.key] / max) * 100)}%` }));
  }, [usersSummary]);

  const therapistItems = React.useMemo(() => {
    const top = therapistsSummary?.top_therapists || [];
    if (!top.length) return [];
    const max = Math.max(...top.map((t) => t.patient_count), 1);
    return top.map((t) => ({ key: t.id, name: t.therapist_name, count: t.patient_count, width: `${Math.max(4, (t.patient_count / max) * 100)}%` }));
  }, [therapistsSummary]);

  const revenueChart = React.useMemo(() => {
    if (!revenueTrends) return null;
    const values = revenueTrends.months.map((m, i) => ({ m: formatMonthLabel(m), v: revenueTrends.revenue[i] }));
    return { ...buildLineChart(values, 1160, 220, 10, 20), values };
  }, [revenueTrends]);

  const formSubmissionItems = React.useMemo(() => {
    if (!formSubmissions) return [];
    const values = formSubmissions.months.map((m, i) => ({ m: formatMonthLabel(m), v: formSubmissions.submissions[i] }));
    const max = Math.max(...values.map((v) => v.v), 1) * 1.05;
    const colors = [C.primary, C.tint1, C.tint2, C.primary, C.tint1, C.ink];
    return values.map((v, i) => ({ ...v, color: colors[i % colors.length], height: `${Math.max(4, (v.v / max) * 100)}%` }));
  }, [formSubmissions]);

  const labStages = React.useMemo(() => {
    if (!labTurnaround) return [];
    const stages = [
      { key: "a", label: "Order → Submission", days: labTurnaround.avg_days_order_to_submission, color: C.tint1, bold: false },
      { key: "b", label: "Submission → Result", days: labTurnaround.avg_days_submission_to_result, color: C.tint2, bold: false },
      { key: "c", label: "Total turnaround", days: labTurnaround.avg_days_total, color: C.primary, bold: true },
    ];
    const max = Math.max(...stages.map((s) => s.days), 1);
    return stages.map((s) => ({ ...s, width: `${Math.max(3, (s.days / max) * 100)}%` }));
  }, [labTurnaround]);

  const registrationHasData = registrationTrends && (registrationTrends.patients.some((v) => v > 0) || registrationTrends.therapists.some((v) => v > 0));
  const registrationMonths = registrationTrends ? registrationTrends.months.map(formatMonthLabel) : [];

  const handleAddSample = () => navigate("/qrscanner");
  const handleAddResults = () => navigate("/ocr");

  return (
    <Box sx={{ fontFamily: FONT, margin: { xs: -2, md: -3 }, minHeight: "100vh", background: C.bg, padding: { xs: "20px 16px 40px", md: "40px 48px 80px" } }}>
      {/* Top bar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "28px", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 22,
              height: 22,
              display: "inline-block",
              flexShrink: 0,
              backgroundColor: "#000000",
              mask: "url(/icons/ic_dashboard.svg) center / contain no-repeat",
              WebkitMask: "url(/icons/ic_dashboard.svg) center / contain no-repeat",
            }}
          />
          <Box>
            <Typography sx={{ fontSize: "23px", fontWeight: 800, color: C.ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>Dashboard</Typography>
          </Box>
          <Box sx={{ ml: "8px" }}>
            <StatusPill connected={socketConnected} />
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<AppsOutlined sx={{ fontSize: 17 }} />}
            onClick={handleAddResults}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              px: "22px",
              py: "13px",
              fontSize: 14,
              fontWeight: 600,
              border: "1px solid rgba(40,40,40,0.14)",
              background: "#fff",
              color: C.ink,
              "&:hover": { background: "#fafafa", border: "1px solid rgba(40,40,40,0.14)" },
            }}
          >
            Add Results
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add sx={{ fontSize: 17 }} />}
            onClick={handleAddSample}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              px: "24px",
              py: "13px",
              fontSize: 14,
              fontWeight: 700,
              background: C.primary,
              boxShadow: "0 12px 26px rgba(22,73,138,0.28)",
              "&:hover": { background: "#0F3468", boxShadow: "0 12px 26px rgba(22,73,138,0.28)" },
            }}
          >
            Add Sample
          </Button>
        </Stack>
      </Box>

      {data.loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 360 }}>
          <CircularProgress size={40} sx={{ color: C.ink }} />
        </Box>
      )}

      {data.error && (
        <Box sx={{ ...cardSx, border: "1px solid #fecaca", background: "#fef2f2", p: 4, textAlign: "center" }}>
          <Typography sx={{ mb: 1, fontWeight: 700, color: "#b91c1c" }}>Error loading dashboard data</Typography>
          <Typography sx={{ color: "#b91c1c" }}>{data.error}</Typography>
        </Box>
      )}

      {!data.loading && !data.error && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gridAutoRows: "80px", gap: "20px" }}>
          {/* Row A: hero + 3 stats + fulfillment */}
          {isAdmin && (
            <Box
              sx={{
                gridColumn: { xs: "span 12", md: "span 4" },
                gridRow: "span 2",
                borderRadius: "24px",
                background: "#163666",
                color: "#fff",
                p: "26px 28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>Total Revenue</Typography>
              </Stack>
              <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em" }}>
                {monthlyOrders ? `€${Math.round(monthlyOrders.total_revenue).toLocaleString()}` : "—"}
              </Typography>
            </Box>
          )}

          {isAdmin ? (
            <StatCardLight
              gridColumn={{ xs: "span 6", md: "span 2" }}
              icon={<Inventory2Outlined sx={{ fontSize: 15, color: C.primary }} />}
              value={monthlyOrders ? monthlyOrders.total_orders.toLocaleString() : "—"}
              label="Total Orders"
            />
          ) : (
            <Box
              sx={{
                gridColumn: { xs: "span 12", md: "span 4" },
                gridRow: "span 2",
                borderRadius: "24px",
                background: "#163666",
                color: "#fff",
                p: "26px 28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Inventory2Outlined sx={{ fontSize: 15, color: "#fff" }} />
                </Box>
              </Stack>
              <Box>
                <Typography sx={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em" }}>
                  {monthlyOrders ? monthlyOrders.total_orders.toLocaleString() : "—"}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)", mt: "2px" }}>Total Orders</Typography>
              </Box>
            </Box>
          )}
          <StatCardLight
            gridColumn={{ xs: "span 6", md: isAdmin ? "span 2" : "span 3" }}
            icon={<CheckCircleOutline sx={{ fontSize: 15, color: C.primary }} />}
            value={monthlyOrders ? monthlyOrders.paid_orders.toLocaleString() : "—"}
            label="Paid Orders"
          />
          <StatCardLight
            gridColumn={{ xs: "span 6", md: isAdmin ? "span 2" : "span 3" }}
            icon={<LocalShippingOutlined sx={{ fontSize: 15, color: C.primary }} />}
            value={monthlyOrders ? monthlyOrders.fulfilled_orders.toLocaleString() : "—"}
            label="Fulfilled Orders"
          />

          <Box sx={{ ...cardSx, gridColumn: { xs: "span 6", md: "span 2" }, gridRow: "span 2", p: 2.5, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <FulfillmentRing pct={fulfillmentPct} />
            <Typography sx={{ fontSize: 12, color: C.textSecondary, textAlign: "center" }}>Fulfillment rate</Typography>
          </Box>

          {/* Row B: country + pipeline */}
          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 5" }, gridRow: "span 5", p: "30px 32px", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            <CardHeader
              title="Orders by Country"
              subtitle="Geographic distribution"
              right={countryData && <Pill>{countryData.unique_countries} countries</Pill>}
            />
            {countryItems.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", flex: 1, minHeight: 0, width: "100%" }}>
                <Box
                  component="svg"
                  viewBox="0 0 180 180"
                  sx={{ width: 132, height: 132, flexShrink: 0, display: "block" }}
                  onMouseLeave={() => setHoverCountry(null)}
                >
                  {countryItems.map((c) => {
                    const active = hoverCountry === c.key;
                    const dimmed = hoverCountry != null && !active;
                    return (
                      <path
                        key={c.key}
                        d={donutSegmentPath(90, 90, 90, 59, c.startPct, c.endPct)}
                        fill={c.color}
                        opacity={dimmed ? 0.28 : 1}
                        stroke={active ? "#fff" : "none"}
                        strokeWidth={active ? 2 : 0}
                        style={{ cursor: "pointer", transition: "opacity 0.18s ease" }}
                        onMouseEnter={() => setHoverCountry(c.key)}
                      />
                    );
                  })}
                  <circle cx={90} cy={90} r={59} fill="#fff" />
                  <text x={90} y={86} textAnchor="middle" style={{ fontSize: 28, fontWeight: 800, fill: C.ink, fontFamily: FONT }}>
                    {countryData.unique_countries}
                  </text>
                  <text x={90} y={106} textAnchor="middle" style={{ fontSize: 12, fill: C.textSecondary, fontFamily: FONT }}>
                    Countries
                  </text>
                </Box>
                <Stack
                  spacing={"4px"}
                  onMouseLeave={() => setHoverCountry(null)}
                  sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    pr: "4px",
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-thumb": { background: C.gray5, borderRadius: 4 },
                  }}
                >
                  {countryItems.map((c) => {
                    const active = hoverCountry === c.key;
                    const dimmed = hoverCountry != null && !active;
                    return (
                      <Box
                        key={c.key}
                        onMouseEnter={() => setHoverCountry(c.key)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          px: "8px",
                          py: "9px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          background: active ? C.tintBg : "transparent",
                          opacity: dimmed ? 0.4 : 1,
                          transition: "background 0.15s ease, opacity 0.15s ease",
                          "&:hover": { background: C.tintBg },
                        }}
                      >
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: c.color, flexShrink: 0, transform: active ? "scale(1.25)" : "scale(1)", transition: "transform 0.15s ease" }} />
                        <Typography sx={{ fontSize: 14, color: C.textBody, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: active ? 700 : 400 }}>{c.name}</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{c.pct.toFixed(1)}%</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <EmptyNote>No country data available</EmptyNote>
            )}
          </Box>

          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 7" }, gridRow: "span 5", p: { xs: "16px", sm: "24px", md: "32px" } }}>
            <CardHeader title="Order Pipeline" subtitle="All-time status breakdown, across every order" />
            {pipelineItems.length > 0 ? (
              <Stack spacing="19px">
                {pipelineItems.map((s) => (
                  <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: { xs: "8px", sm: "14px" } }}>
                    <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: C.textBody, width: { xs: 88, sm: 140, md: 180 }, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</Typography>
                    <Box sx={{ flex: 1, minWidth: 0, height: 8, borderRadius: "999px", background: C.bg, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", borderRadius: "999px", background: s.color, width: s.width, ...barLivingSx }} />
                    </Box>
                    <Typography sx={{ fontSize: { xs: 11, sm: 13 }, fontWeight: 600, color: C.ink, width: { xs: 76, sm: 100, md: 120 }, textAlign: "right", flexShrink: 0 }}>
                      {s.count.toLocaleString()} · {s.pct}%
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyNote>No pipeline data available</EmptyNote>
            )}
          </Box>

          {/* Row C: users + therapists */}
          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 5" }, gridRow: "span 5", p: "32px", display: "flex", flexDirection: "column" }}>
            <CardHeader title="Users" subtitle="Admin dashboard accounts" />
            <Stack direction="row" spacing={4} sx={{ mb: "30px" }}>
              <Box>
                <Typography sx={{ fontSize: 32, fontWeight: 800, color: C.ink }}>{usersSummary?.total_users ?? "—"}</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "4px" }}>Total</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 32, fontWeight: 800, color: C.ink }}>{usersSummary?.active_users ?? "—"}</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "4px" }}>Active</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 32, fontWeight: 800, color: C.ink }}>{usersSummary?.registered_last_30_days ?? "—"}</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "4px" }}>New (30d)</Typography>
              </Box>
            </Stack>
            {usersRoleItems.length > 0 ? (
              <Stack spacing="20px" sx={{ flex: 1, justifyContent: "center" }}>
                {usersRoleItems.map((r) => (
                  <Box key={r.key} sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, color: C.textBody, width: 90, flexShrink: 0 }}>{r.name}</Typography>
                    <Box sx={{ flex: 1, height: 9, borderRadius: "999px", background: C.bg, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", borderRadius: "999px", background: r.color, width: r.width, ...barLivingSx }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.ink, width: 20, textAlign: "right" }}>{r.count}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyNote>No user data available</EmptyNote>
            )}
          </Box>

          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 7" }, gridRow: "span 5", p: { xs: "16px", sm: "24px", md: "32px" }, display: "flex", flexDirection: "column" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "flex-start" }}
              justifyContent="space-between"
              sx={{ mb: "20px", gap: { xs: 1.5, sm: 2 } }}
            >
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>Therapists</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>Top therapists by patient count</Typography>
              </Box>
              <Stack direction="row" spacing={{ xs: "16px", sm: "22px" }}>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{therapistsSummary?.total_therapists ?? "—"}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.textSecondary }}>Total</Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.primary }}>{therapistsSummary?.verified ?? "—"}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.textSecondary }}>Verified</Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.gray1 }}>{therapistsSummary?.unverified ?? "—"}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.textSecondary }}>Unverified</Typography>
                </Box>
              </Stack>
            </Stack>
            {therapistItems.length > 0 ? (
              <Stack spacing="12px" sx={{ overflow: "auto", flex: 1 }}>
                {therapistItems.map((t) => (
                  <Box key={t.key} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Typography sx={{ fontSize: 14, color: C.textBody, width: 190, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</Typography>
                    <Box sx={{ flex: 1, height: 8, borderRadius: "999px", background: C.bg, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", borderRadius: "999px", background: C.primary, width: t.width, ...barLivingSx }} />
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink, width: 32, textAlign: "right" }}>{t.count}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyNote>No therapist ranking data yet</EmptyNote>
            )}
          </Box>

          {/* Row D: revenue trends */}
          {isAdmin && (
          <Box sx={{ ...cardSx, gridColumn: "span 12", gridRow: "span 4", p: "32px" }}>
            <CardHeader title="Revenue Trends" subtitle={`Monthly revenue · last ${revenueTrends?.months?.length || 12} months`} />
            {revenueChart && revenueChart.points.length > 1 ? (
              <>
              <Box sx={{ overflowX: { xs: "auto", md: "visible" } }}>
              <Box sx={{ minWidth: { xs: 700, md: "100%" } }}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="svg"
                    viewBox="0 0 1160 220"
                    sx={{ width: "100%", height: 200, display: "block", overflow: "visible" }}
                    onMouseLeave={() => setHoverRevenue(null)}
                  >
                    {revenueChart.gridlines.map((g, i) => (
                      <line key={i} x1={0} x2={1160} y1={g.y} y2={g.y} stroke="#EEEEEE" strokeWidth={1} />
                    ))}
                    <defs>
                      <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.primary} stopOpacity="0.18" />
                        <stop offset="100%" stopColor={C.primary} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d={revenueChart.areaPath} fill="url(#revFill)" />
                    <path d={revenueChart.linePath} fill="none" stroke={C.primary} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
                    {hoverRevenue != null && (
                      <line
                        x1={revenueChart.points[hoverRevenue].x}
                        x2={revenueChart.points[hoverRevenue].x}
                        y1={10}
                        y2={200}
                        stroke={C.tint2}
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                      />
                    )}
                    {revenueChart.points.map((p, i) => {
                      const active = hoverRevenue === i;
                      return (
                        <g key={i} onMouseEnter={() => setHoverRevenue(i)} style={{ cursor: "pointer" }}>
                          <circle cx={p.x} cy={p.y} r={18} fill="transparent" />
                          <circle
                            cx={p.x}
                            cy={p.y}
                            r={active ? 6 : 3.6}
                            fill="#fff"
                            stroke={C.primary}
                            strokeWidth={active ? 3 : 2.4}
                            style={{ transition: "r 0.15s ease" }}
                          />
                        </g>
                      );
                    })}
                  </Box>
                  {hoverRevenue != null && revenueChart.points[hoverRevenue] && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: `clamp(0%, calc(${(revenueChart.points[hoverRevenue].x / 1160) * 100}% - 48px), calc(100% - 96px))`,
                        background: C.ink,
                        color: "#fff",
                        px: "12px",
                        py: "8px",
                        borderRadius: "10px",
                        pointerEvents: "none",
                        boxShadow: "0 8px 20px rgba(40,40,40,0.18)",
                        minWidth: 88,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)", lineHeight: 1.2 }}>{revenueChart.points[hoverRevenue].m}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
                        €{Math.round(revenueChart.points[hoverRevenue].v).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: "6px", px: "2px" }}>
                  {revenueChart.points.map((p, i) => (
                    <Typography
                      key={i}
                      onMouseEnter={() => setHoverRevenue(i)}
                      onMouseLeave={() => setHoverRevenue(null)}
                      sx={{
                        fontSize: 12,
                        color: hoverRevenue === i ? C.ink : C.textFaint,
                        fontWeight: hoverRevenue === i ? 700 : 500,
                        cursor: "pointer",
                        transition: "color 0.15s ease",
                      }}
                    >
                      {p.m}
                    </Typography>
                  ))}
                </Stack>
              </Box>
              </Box>
              </>
            ) : (
              <EmptyNote>No revenue data available</EmptyNote>
            )}
          </Box>
          )}

          {/* Row E: form submissions + lab turnaround + recent activity */}
          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 4" }, gridRow: "span 6", p: "32px", display: "flex", flexDirection: "column" }}>
            <CardHeader title="Form Submissions" subtitle="SIBO intake forms by month" />
            {formSubmissionItems.length > 0 ? (
              <>
                <Stack direction="row" alignItems="flex-end" spacing="14px" sx={{ height: 170, mb: "10px" }}>
                  {formSubmissionItems.map((f, i) => (
                    <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", height: "100%", justifyContent: "flex-end" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{f.v}</Typography>
                      <Box sx={{ width: "100%", maxWidth: 40, borderRadius: "8px 8px 3px 3px", background: f.color, height: f.height, ...colBarLivingSx }} />
                      <Typography sx={{ fontSize: 11, color: C.textFaint }}>{f.m}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Stack spacing="20px" sx={{ mt: "20px", pt: "24px", borderTop: `1px solid ${C.border}`, flex: 1, justifyContent: "center" }}>
                  {[
                    ["Consent rate", formSubmissions?.consent_rate, C.primary],
                    ["Linked to a therapist", formSubmissions?.with_therapist_linked, C.tint2],
                  ].map(([label, value, color]) => {
                    const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
                    return (
                      <Box key={label}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: "7px" }}>
                          <Typography sx={{ fontSize: 13, color: C.textBody }}>{label}</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{pct.toFixed(1)}%</Typography>
                        </Stack>
                        <Box sx={{ height: 8, borderRadius: "999px", background: C.bg, overflow: "hidden" }}>
                          <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: "999px", background: color, ...barLivingSx }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            ) : (
              <EmptyNote>No submission data available</EmptyNote>
            )}
          </Box>

          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 3" }, gridRow: "span 6", p: "30px", display: "flex", flexDirection: "column" }}>
            <Box sx={{ mb: "30px" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.ink }}>Lab Turnaround</Typography>
              <Typography sx={{ fontSize: 12, color: C.textSecondary, mt: "2px" }}>Average days per stage</Typography>
            </Box>
            {labStages.length > 0 ? (
              <Stack spacing="34px" sx={{ flex: 1, justifyContent: "center" }}>
                {labStages.map((s) => (
                  <Box key={s.key}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: "9px" }}>
                      <Typography sx={{ fontSize: 13, color: C.textBody, fontWeight: s.bold ? 700 : 500 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{s.days.toFixed(1)}d</Typography>
                    </Stack>
                    <Box sx={{ height: 9, borderRadius: "999px", background: C.bg, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", borderRadius: "999px", background: s.color, width: s.width, ...barLivingSx }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <EmptyNote>No turnaround data available</EmptyNote>
            )}
          </Box>

          <Box sx={{ ...cardSx, gridColumn: { xs: "span 12", md: "span 5" }, gridRow: "span 6", p: "32px", display: "flex", flexDirection: "column" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: "18px", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>Recent Activity</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>Live order &amp; operator updates</Typography>
              </Box>
              <StatusPill connected={socketConnected} />
            </Stack>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", overflow: "auto", flex: 1 }}>
              {recentActivities.length > 0 ? (
                recentActivities.slice(0, 6).map((activity, index) => (
                  <ActivityRow key={activity.id} activity={activity} index={index} isNew={newActivityIds.has(activity.id)} />
                ))
              ) : (
                <EmptyNote>No recent activities</EmptyNote>
              )}
            </Box>
            {recentActivities.length > 0 && (
              <Box
                component="button"
                type="button"
                onClick={() => setActivityDialogOpen(true)}
                sx={{
                  all: "unset",
                  display: "inline-block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.primary,
                  cursor: "pointer",
                  mt: "14px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                View all activity →
              </Box>
            )}
          </Box>

          {/* Row F: registration trends */}
          <Box sx={{ ...cardSx, gridColumn: "span 12", gridRow: "span 3", p: "32px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "18px", gap: 1.5, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>Registration Trends</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>New patient &amp; therapist signups by month</Typography>
              </Box>
              <Stack direction="row" spacing="18px">
                <Stack direction="row" alignItems="center" spacing="6px">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: C.primary }} />
                  <Typography sx={{ fontSize: 13, color: C.textBody }}>Patients</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing="6px">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: C.ink }} />
                  <Typography sx={{ fontSize: 13, color: C.textBody }}>Therapists</Typography>
                </Stack>
              </Stack>
            </Stack>
            <Box component="svg" viewBox="0 0 1160 100" sx={{ width: "100%", height: 80, display: "block" }} onMouseLeave={() => setHoverReg(null)}>
              {[20, 50, 80].map((y) => (
                <line key={y} x1={0} x2={1160} y1={y} y2={y} stroke="#EEEEEE" strokeWidth={1} />
              ))}
              <line x1={0} y1={86} x2={1160} y2={86} stroke="#D9D9D9" strokeWidth={2} />
              {registrationHasData &&
                registrationTrends.months.map((_, i) => {
                  const dx = 1160 / registrationTrends.months.length;
                  const maxV = Math.max(...registrationTrends.patients, ...registrationTrends.therapists, 1);
                  const barW = dx * 0.28;
                  const px = i * dx + dx * 0.3;
                  const tx = i * dx + dx * 0.55;
                  const ph = (registrationTrends.patients[i] / maxV) * 66;
                  const th = (registrationTrends.therapists[i] / maxV) * 66;
                  const active = hoverReg === i;
                  const dimmed = hoverReg != null && !active;
                  return (
                    <g
                      key={i}
                      onMouseEnter={() => setHoverReg(i)}
                      style={{ cursor: "pointer", opacity: dimmed ? 0.35 : 1, transition: "opacity 0.15s ease" }}
                    >
                      <rect x={px} y={86 - ph} width={barW} height={ph} rx={3} fill={C.primary} style={{ transformOrigin: `${px + barW / 2}px 86px`, transform: active ? "scaleY(1.08)" : "scaleY(1)" }} />
                      <rect x={tx} y={86 - th} width={barW} height={th} rx={3} fill={C.ink} style={{ transformOrigin: `${tx + barW / 2}px 86px`, transform: active ? "scaleY(1.08)" : "scaleY(1)" }} />
                      {active && (
                        <text x={i * dx + dx * 0.5} y={12} textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: C.ink, fontFamily: FONT }}>
                          {registrationTrends.patients[i]} / {registrationTrends.therapists[i]}
                        </text>
                      )}
                    </g>
                  );
                })}
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: "6px", px: "2px" }}>
              {registrationMonths.map((m, i) => (
                <Typography key={i} sx={{ fontSize: 12, color: C.textFaint, fontWeight: 500 }}>{m}</Typography>
              ))}
            </Stack>
            {!registrationHasData && (
              <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", mt: "12px" }}>
                No new patient or therapist signups recorded in this period
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <ActivityDialog
        open={activityDialogOpen}
        onClose={() => setActivityDialogOpen(false)}
        initialActivities={recentActivities}
        newActivityIds={newActivityIds}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
